import Vue from 'vue'
import Router from 'vue-router'
import store from '../store.js'
import HTTP from '@/http-common.js'

/**
 * Components
 */
import UserPending from '@/components/UserPending'
import OrganisationPending from '@/components/OrganisationPending'
import Dashboard from '@/components/Dashboard'
import Activity from '@/components/Activity'
import Registration from '@/components/Registration'
import Login from '@/components/registration/Login'
import Register from '@/components/registration/Register'
import AcceptInvitation from '@/components/registration/AcceptInvitation'
import Reset from '@/components/registration/Reset'
import UpdatePassword from '@/components/registration/UpdatePassword'
import UpdateEmail from '@/components/registration/UpdateEmail'
import ConfirmPetition from '@/components/ConfirmPetition'

// Resources
import Listing from '@/components/Listing'
import EditItem from '@/components/editor/EditItem'
import Builder from '@/components/editor/Builder'

// Media uploads
import Uploads from '@/components/Uploads'

// Tags
import Tags from '@/components/Tags'
import EditTag from '@/components/editor/EditTag'
import MergeTag from '@/components/editor/MergeTag'

// Organisations
import Organisations from '@/components/Organisations'
import EditOrganisation from '@/components/editor/EditOrganisation'

// Websites
import Websites from '@/components/Websites'
import EditWebsite from '@/components/editor/EditWebsite'

// Settings
import Settings from '@/components/Settings'
import Account from '@/components/settings/Account'
import Organisation from '@/components/settings/Organisation'
import Users from '@/components/Users'
import ActiveUsers from '@/components/users/ActiveUsers'
import EditUser from '@/components/editor/EditUser'
import PendingUsers from '@/components/users/PendingUsers'
import InviteUsers from '@/components/users/InviteUsers'

// Quiz
import Quizes from '@/components/quizes/Quizes.vue'
import EditQuiz from '@/components/quizes/EditQuiz.vue'
import QuizEmbed from '@/components/quizes/QuizEmbed.vue'

// Other
import PageNotFound from '@/components/404'

/**
 * Router
 */
const originalPush = Router.prototype.push
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => err)
}
Vue.use(Router)

// Route data
import { ResourceData, NewsData, ClausesData, CampaignData, EventData } from '@/router/data.js'

// Routes
const listings = [
  {
    plural: 'Blog',
    singular: 'Blog post',
    data: NewsData,
    types: ['blog'],
    builder: true,
    scraper: false,
  },
  {
    plural: 'Pages',
    singular: 'Page',
    data: NewsData,
    types: ['page'],
    builder: true,
    scraper: false,
  },
  {
    plural: 'Campaigns',
    singular: 'Campaign',
    data: CampaignData,
    types: ['campaign'],
    builder: true,
    scraper: false,
  },
  {
    plural: 'Projects',
    singular: 'Project',
    data: NewsData,
    types: ['project'],
    builder: true,
    scraper: false,
  },
  {
    plural: 'News',
    singular: 'News',
    data: NewsData,
    types: ['article'],
  },
  {
    plural: 'Submissions',
    singular: 'Submission',
    types: [
      'archive_mm',
      'audio',
      'image',
      'video',
      'archive_doc',
      'doc',
      'pdf',
      'ppt',
      'xls',
      'article',
      'blog',
      'event',
    ],
    buttons: false,
  },
  {
    plural: 'Publications',
    singular: 'Publication',
    types: ['doc', 'pdf', 'ppt', 'xls'],
    builder: true,
    scraper: false,
  },
  {
    plural: 'Digital publications',
    singular: 'Digital publication',
    types: ['digital_publication'],
    builder: true,
    scraper: false,
  },
  {
    plural: 'Clauses',
    data: ClausesData,
    singular: 'Clause',
    types: ['clause'],
    builder: true,
    scraper: false,
  },
  {
    plural: 'Multimedia',
    singular: 'Multimedia',
    types: ['audio', 'image', 'video'],
    builder: true,
    scraper: false,
  },
  {
    plural: 'Case studies',
    singular: 'Case study',
    data: NewsData,
    types: ['case_study'],
    builder: true,
    scraper: false,
  },
  {
    plural: 'Events',
    singular: 'Event',
    data: EventData,
    types: ['event'],
    builder: true,
    scraper: false,
  },
  {
    plural: 'Lists',
    singular: 'List',
    data: ResourceData,
    types: ['event'],
  },
]

/**
 * Check cookie login data
 */
let routes = []

listings.map(function (a) {
  const ScraperEditor = [
    {
      path: 'scrape',
      component: EditItem,
      name: `Add ${a.singular.toLowerCase()}`,
    },
    {
      path: 'scrape/:id',
      component: EditItem,
      name: `Edit ${a.singular.toLowerCase()}`,
    },
  ]

  const BuilderEditor = [
    {
      path: 'build',
      component: Builder,
      name: `Create ${a.singular.toLowerCase()}`,
    },
    {
      path: 'build/:id',
      component: Builder,
      name: `Manage ${a.singular.toLowerCase()}`,
    },
  ]

  // Create child elements for either scraper, builder or both
  let Children = ScraperEditor
  if (a.builder === true) Children = ScraperEditor.concat(BuilderEditor)
  if (a.scraper === false) Children = BuilderEditor

  // Create UI buttons out of children
  let Buttons = []
  if (a.buttons !== false) {
    Buttons = Children.filter((a) => a.path === 'build' || a.path === 'scrape').map(function (a) {
      const b = {
        label: a.name,
        route: a.name,
      }
      return b
    })
  }

  // Define scraper and builder editor paths
  const Editor = Children.filter((a) => a.path === 'build/:id' || a.path === 'scrape/:id').reduce(function (
    result,
    element
  ) {
    result[element.name.split(' ')[0].toLowerCase()] = element
    return result
  },
  {})

  let b = {}
  b = {
    name: a.plural,
    path: `/${a.plural.replace(/\s+/g, '-').toLowerCase()}`,
    component: Listing,
    children: Children,
    props: {
      title: a.plural,
      buttons: Buttons,
      editor: Editor,
      cells: ResourceData,
      types: a.types,
    },
  }

  if (a.data) b.props.cells = a.data

  routes.push(b)
})

// Other routes
routes.push(
  {
    path: '*',
    component: PageNotFound,
  },
  {
    path: '/login',
    component: Registration,
    children: [
      {
        name: 'Login',
        path: '/login',
        component: Login,
        meta: { hasNav: false, registerWizzard: false },
      },
      {
        name: 'Register',
        path: '/register',
        component: Register,
        meta: { hasNav: false, registerWizzard: true },
      },
      {
        name: 'AcceptInvitation',
        path: '/register/:id',
        component: AcceptInvitation,
        meta: { hasNav: false, registerWizzard: false },
      },
      {
        name: 'Reset',
        path: '/reset',
        component: Reset,
        meta: { hasNav: false, registerWizzard: false },
      },
      {
        name: 'UpdatePassword',
        path: '/update-password/:id',
        component: UpdatePassword,
        meta: { hasNav: false, registerWizzard: false },
      },
      {
        name: 'UpdateEmail',
        path: '/update-email/:id',
        component: UpdateEmail,
        meta: { hasNav: false, registerWizzard: false },
      },
    ],
  },
  {
    name: 'ConfirmPetition',
    path: '/confirm_petition/:token',
    component: ConfirmPetition,
    meta: { hasNav: false },
  },
  {
    name: 'UserPending',
    path: '/pending',
    component: UserPending,
    meta: { hasNav: false },
  },
  {
    name: 'OrganisationPending',
    path: '/pending-org',
    component: OrganisationPending,
    meta: { hasNav: false },
  },
  {
    name: 'Dashboard',
    path: '/',
    component: Dashboard,
  },
  {
    name: 'Activity',
    path: '/activity',
    component: Activity,
  },
  {
    name: 'Tags',
    path: '/tags',
    component: Tags,
    props: {
      types: [
        'issue',
        'sector',
        'organisation',
        'country',
        'campaign',
        'region',
        'type',
        'clause_theme',
        'clause_type',
      ],
    },
    children: [
      {
        name: 'Add tag',
        path: 'add',
        component: EditTag,
      },
      {
        name: 'Merge tags',
        path: 'merge',
        component: MergeTag,
      },
      {
        name: 'Edit tag',
        path: 'edit/:id',
        component: EditTag,
      },
    ],
  },
  {
    name: 'Uploads',
    path: '/uploads',
    component: Uploads,
  },
  {
    name: 'Organisations',
    path: '/organisations',
    component: Organisations,
    children: [
      {
        name: 'Edit organisation',
        path: 'edit/:id',
        component: EditOrganisation,
        children: [
          {
            name: 'Edit organisation user',
            path: 'user/edit/:user_id',
            component: EditUser,
          },
          {
            name: 'Add organisation user',
            path: 'user/add',
            component: EditUser,
          },
          {
            name: 'Edit organisation websites',
            path: 'website/:website_id',
            component: EditWebsite,
          },
        ],
      },
    ],
  },
  {
    name: 'Websites',
    path: '/websites',
    component: Websites,
    children: [
      {
        name: 'Add website',
        path: 'add',
        component: EditWebsite,
      },
      {
        name: 'Edit website',
        path: 'website/:website_id',
        component: EditWebsite,
      },
    ],
  },
  {
    name: 'Users',
    path: '/users/active',
    component: Users,
    children: [
      {
        name: 'Active users',
        path: '/users/active',
        component: ActiveUsers,
        children: [
          {
            name: 'Edit user',
            path: 'user/edit/:user_id',
            component: EditUser,
          },
          {
            name: 'Add user',
            path: 'user/add',
            component: EditUser,
          },
        ],
      },
      {
        name: 'Pending users',
        path: '/users/pending',
        component: PendingUsers,
        children: [
          {
            name: 'Edit pending user',
            path: 'user/edit/:user_id',
            component: EditUser,
          },
        ],
      },
      {
        name: 'Invite users',
        path: '/users/invite',
        component: InviteUsers,
      },
    ],
  },
  {
    name: 'Settings',
    path: '/settings/account',
    component: Settings,
    children: [
      {
        name: 'Account',
        path: '/settings/account',
        component: Account,
      },
      {
        name: 'Organisation',
        path: '/settings/organisation',
        component: Organisation,
      },
    ],
  },
  {
    name: 'Quizes',
    path: '/quizes',
    component: Quizes,
    children: [
      {
        name: 'Edit quiz',
        path: '/quizes/edit/:id',
        component: EditQuiz,
      },
      {
        name: 'Add quiz',
        path: '/quizes/add',
        component: EditQuiz,
      },
    ],
  },
  {
    name: 'QuizEmbedabble',
    path: '/embed/quiz/:id',
    component: QuizEmbed,
    meta: {
      isRoot: true,
    },
  }
)

const router = new Router({
  routes,
})

const registrationRoutes = [
  'Login',
  'Reset',
  'Register',
  'AcceptInvitation',
  'UpdatePassword',
  'UpdateEmail',
  'ConfirmPetition',
]

const publicRoutes = ['QuizEmbedabble']

router.beforeEach(async (to, _, next) => {
  if (publicRoutes.includes(to.name)) {
    next()
    return
  }

  const cookie = localStorage && localStorage.getItem('token')
  if (cookie === null && !registrationRoutes.includes(to.name)) {
    // No cookie whatsoever, redirect to login immediately
    next('/login')
  } else if (cookie === null && registrationRoutes.includes(to.name)) {
    // No cookie whatsoever and its in registration routes allow him to visit
    next()
  } else {
    // Load user data
    store.dispatch('setGlobalLoader', true)

    if (!store.state.accounts.user.id) {
      await HTTP.get('me')
        .then((response) => {
          store.dispatch('updateUser', response.data)
        })
        .catch(() => {
          // Remove token and redirect to login if user unauth
          store.dispatch('setGlobalLoader', false)
          localStorage.removeItem('token')
          next('/login')
        })
    }

    // Redirect user if status is pending
    if (store.state.accounts.user.status === 'pending' && to.name !== 'UserPending') {
      store.dispatch('setGlobalLoader', false)
      next('/pending')
      // Avoid landing on registrational pages
    } else if (registrationRoutes.includes(to.name) && to.name !== 'ConfirmPetition') {
      store.dispatch('setGlobalLoader', false)
      next('/')
      // Update user based on cookie data, get organisation data
    } else {
      if (!store.state.accounts.org.id) {
        store
          .dispatch('loadOrg', store.state.accounts.user.org_id)
          .then(() => {
            store.dispatch('setGlobalLoader', false)
            if (store.state.accounts.org.status === 'pending') {
              next('/pending-org')
            } else {
              next()
            }
          })
          .catch((e) => {
            store.dispatch('setGlobalLoader', false)
            localStorage.removeItem('token')

            if (e.response.status === 401 && e.response.data.message === 'unauthorized') {
              next('/login')
            } else if (e.response.status === 401 && e.response.data.message === 'pending') {
              next('/pending-org')
            } else {
              next('/login')
            }
          })
      } else {
        store.dispatch('setGlobalLoader', false)
        next()
      }
    }
  }
})

router.afterEach(async (to) => {
  const siteName = 'Motyrõ'
  const pageTitle = to.name

  if (pageTitle === null) {
    document.title = siteName
  } else {
    document.title = `${siteName} \u2014 ${pageTitle}`
  }
})

export default router
